// DevTracker Notification Template Library (v2.2.0)
// Standardized Professional Messages for DevTracker Platform

export const NOTIFICATION_TEMPLATES = {
  // 🟢 Success Messages (Green)
  ACCOUNT_CREATED: {
    title: "Account Created",
    message: "Hello,\n\nYour account has been successfully created. Welcome to the platform. You can now access your dashboard, manage settings, and start exploring available features.\n\n— DevTracker Team\nBy: Origin Core / AI",
    type: "success"
  },
  PROFILE_UPDATED: {
    title: "Profile Updated",
    message: "Hello,\n\nYour profile information has been successfully updated. These changes are now reflected across the platform and in your public developer footprint.\n\n— DevTracker Team",
    type: "success"
  },
  LOGIN_SUCCESSFUL: {
    title: "Login Successful",
    message: "Hello,\n\nYou have successfully signed in to your portal. Your workspace is synchronized and ready for activity monitoring.\n\n— DevTracker Team",
    type: "success"
  },
  PASSWORD_CHANGED: {
    title: "Password Changed",
    message: "Hello,\n\nYour security credentials have been updated. If you did not authorize this change, please contact support immediately.\n\n— DevTracker Team",
    type: "success"
  },
  EMAIL_VERIFIED: {
    title: "Email Verified",
    message: "Hello,\n\nThank you for verifying your email. Your account identity is now confirmed and all advanced features have been unlocked.\n\n— DevTracker Team",
    type: "success"
  },
  INTEGRATION_CONNECTED: {
    title: "Integration Connected",
    message: "Hello,\n\nThe selected integration has been successfully connected to your account. Your development activity will now be automatically synchronized.\n\n— DevTracker Team\nBy: Origin Core / AI",
    type: "success"
  },
  SETTINGS_SAVED: {
    title: "Settings Saved",
    message: "Hello,\n\nYour platform configuration settings have been saved successfully. Your environment is now optimized according to your preferences.\n\n— DevTracker Team",
    type: "success"
  },
  UPLOAD_COMPLETED: {
    title: "Upload Completed",
    message: "Hello,\n\nYour data upload has been processed successfully and is now safely stored in the DevTracker cloud.\n\n— DevTracker Team",
    type: "success"
  },
  CHANGES_PUBLISHED: {
    title: "Changes Published",
    message: "Hello,\n\nYour latest changes have been successfully published. Your public profile and embeds have been updated to reflect the new data.\n\n— DevTracker Team",
    type: "success"
  },
  ACTION_COMPLETED: {
    title: "Action Completed",
    message: "Hello,\n\nThe requested administrative action has been completed successfully. Your account state has been updated accordingly.\n\n— DevTracker Team",
    type: "success"
  },
  ID_CHANGE_APPROVED: {
    title: "🆔 Profile ID Migrated",
    message: "Congratulations!\n\nYour Profile ID change request has been approved and executed. Your new identifier is now active across the platform.\n\nNew ID: {newId}\nCase ID: {caseId}\n\n— DevTracker Security Board",
    type: "success"
  },
  ID_CHANGE_FEE: {
    title: "💎 AI Credits Deducted",
    message: "An administrative fee of 20 AI Credits has been deducted for your Profile ID migration request (Case: {caseId}).\n\n— Origin Core Billing",
    type: "info"
  },
  ID_CHANGE_REJECTED: {
    title: "⚠️ ID Migration Rejected",
    message: "Your request to change your Profile ID (Case: {caseId}) has been rejected. \n\nReason: {reason}\n\nNote: If credits were deducted, they have been restored to your balance.\n\n— DevTracker Security Board",
    type: "warning"
  },
  ID_CHANGE_CANCELLED: {
    title: "📁 Case Closed",
    message: "The migration request for Case ID {caseId} has been closed or cancelled by an administrator.\n\n— DevTracker Support",
    type: "info"
  },

  // 🔵 Info Messages (Blue)
  WELCOME_MESSAGE: {
    title: "Welcome to DevTracker",
    message: "Hello Developer,\n\nWelcome to the platform! We are excited to help you track and showcase your development journey. Explore your dashboard to see your current coding statistics.\n\nNext Steps:\n• Complete your profile setup\n• Connect your first platform\n\n— DevTracker Team",
    type: "info"
  },
  NEW_FEATURE: {
    title: "New Feature Available",
    message: "Hello,\n\nWe have just released a new feature in your dashboard designed to enhance your performance analytics. Visit the 'AI Insights' tab to see it in action.\n\n— DevTracker Team",
    type: "info"
  },
  SYSTEM_UPDATE: {
    title: "Platform Evolution (v2.5.3)",
    message: "Major update: High-fidelity Case Journey tracking and visual timeline monitors are now live.\n\n— DevTracker Team\nBy: Origin Core / AI",
    type: "info"
  },
  DOCS_AVAILABLE: {
    title: "Documentation Available",
    message: "Hello,\n\nComprehensive documentation is now available to help you master the DevTracker platform.\n\n— DevTracker Team\nBy: Origin Core / AI",
    type: "info"
  },
  PROFILE_INCOMPLETE_REMINDER: {
    title: "Profile Incomplete",
    message: "Hello,\n\nJust a reminder to finish your profile configuration. A complete profile allows for more accurate AI insights and better career trajectory forecasting.\n\n— DevTracker Team",
    type: "info"
  },
  NEW_NOTIFICATION: {
    title: "New Notification",
    message: "Hello,\n\nYou have a new high-priority broadcast waiting for your review in the platform hub.\n\n— DevTracker Team",
    type: "info"
  },
  ACCOUNT_ACTIVITY: {
    title: "Recent Account Activity",
    message: "Hello,\n\nOur security system detected a successful login to your account. If this was not you, please rotate your security credentials in the settings tab.\n\n— DevTracker Team",
    type: "info"
  },
  PLATFORM_SYNC: {
    title: "Platform Sync",
    message: "Hello,\n\nYour data is currently being synchronized with external platforms. You may experience slight delays in metric updates until the sync is complete.\n\n— DevTracker Team",
    type: "info"
  },
  DASHBOARD_READY: {
    title: "Dashboard Ready",
    message: "Hello Developer,\n\nYour personalized dashboard has been successfully initialized with the latest data from your connected footprints.\n\n— DevTracker Team",
    type: "info"
  },

  // 🟠 Warning Messages (Orange)
  PROFILE_INCOMPLETE: {
    title: "Profile Incomplete",
    message: "Hello,\n\nYour developer profile is currently missing essential information. Complete your setup to ensure accurate activity tracking.\n\nAction Required:\n• Visit Profile Settings and fill in required fields.\n\n— DevTracker Team",
    type: "warning"
  },
  INTEGRATION_MISSING: {
    title: "Integration Missing",
    message: "Hello Developer,\n\nNo external coding platforms are connected to your account yet. To unlock the full power of DevTracker, please link your workspace.\n\nAction Required:\n• Connect GitHub, LeetCode, or Codeforces in the Platform tab.\n\n— DevTracker Team",
    type: "warning"
  },
  SESSION_EXPIRING: {
    title: "Session Expiring Soon",
    message: "Hello,\n\nYour security session is nearing expiration. To prevent unsaved changes from being lost, please re-authenticate.\n\nAction Required:\n• Refresh the page or log in again to continue.\n\n— DevTracker Team",
    type: "warning"
  },
  PASSWORD_WEAK: {
    title: "Password Weak",
    message: "Hello,\n\nOur security audit has flagged your current password as weak. We recommend updating it to protect your developer data.\n\nAction Required:\n• Update your password in the Security tab.\n\n— DevTracker Team",
    type: "warning"
  },
  STORAGE_LOW: {
    title: "Storage Nearly Full",
    message: "Hello,\n\nYour account data storage has reached 90% capacity. New integration syncs may be throttled soon.\n\nAction Required:\n• Review and manage your stored developer metrics.\n\n— DevTracker Team",
    type: "warning"
  },
  ACTION_REQUIRED_WARNING: {
    title: "Action Required",
    message: "Hello,\n\nA pending task requires your immediate attention to avoid platform service interruption.\n\nAction Required:\n• Review the recent alerts in your Security Hub.\n\n— DevTracker Team",
    type: "warning"
  },
  UNVERIFIED_EMAIL: {
    title: "Unverified Email",
    message: "Hello,\n\nYour email address has not been verified yet. Please confirm your identity to enable all platform features.\n\nAction Required:\n• Check your inbox for the verification link sent by our team.\n\n— DevTracker Team",
    type: "warning"
  },
  CONNECTION_INTERRUPTED: {
    title: "Connection Interrupted",
    message: "Hello,\n\nThe synchronization between DevTracker and your external platforms was interrupted due to a network anomaly.\n\nAction Required:\n• Manually re-trigger a sync from your dashboard settings.\n\n— DevTracker Team",
    type: "warning"
  },
  PENDING_UPDATES: {
    title: "Pending Updates",
    message: "Hello,\n\nYou have pending account or security updates waiting for your approval in the admin portal.\n\nAction Required:\n• Visit the Platform tab to review and apply changes.\n\n— DevTracker Team",
    type: "warning"
  },

  // 🔴 Critical / Error Messages (Red)
  LOGIN_FAILED: {
    title: "Login Failed",
    message: "Hello,\n\nA failed login attempt was recorded for your account. If you have forgotten your password, please use the recovery tool.\n\nAction Required:\n• Verify your credentials or trigger a password reset.\n\n— DevTracker Team",
    type: "critical"
  },
  UNAUTHORIZED_ACCESS: {
    title: "Unauthorized Access",
    message: "Hello,\n\nAn attempt to access a restricted resource was blocked. Your security level does not permit this action.\n\n— DevTracker Team",
    type: "critical"
  },
  SESSION_EXPIRED: {
    title: "Session Expired",
    message: "Hello,\n\nYour secure session has expired for your protection. Please log in again to resume your development activity tracking.\n\nAction Required:\n• Return to the login page to re-authenticate.\n\n— DevTracker Team",
    type: "critical"
  },
  ACCOUNT_LOCKED: {
    title: "Account Locked",
    message: "Hello Developer,\n\nYour account has been temporarily locked due to multiple failed verification attempts. Please contact our support staff to verify your identity.\n\nAction Required:\n• Open a support ticket to unlock your profile.\n\n— DevTracker Team",
    type: "critical"
  },
  SUSPICIOUS_ACTIVITY: {
    title: "Suspicious Activity Detected",
    message: "Hello,\n\nOur real-time security monitor has flagged unusual activity patterns originating from your account ID.\n\nAction Required:\n• Review your active sessions and clear any unauthorized footprints.\n\n— DevTracker Team",
    type: "critical"
  },
  VERIFICATION_FAILED: {
    title: "Verification Failed",
    message: "Hello,\n\nWe were unable to verify your identity using the provided token. The verification link may have expired or been deactivated.\n\nAction Required:\n• Request a new verification link from your account settings.\n\n— DevTracker Team",
    type: "critical"
  },
  UPLOAD_FAILED: {
    title: "Upload Failed",
    message: "Hello,\n\nA critical error occurred during the data transmission process. Your files were not saved.\n\nAction Required:\n• Check your network stability and attempt the upload again.\n\n— DevTracker Team",
    type: "critical"
  },
  CONNECTION_FAILED: {
    title: "Connection Failed",
    message: "Hello,\n\nDevTracker was unable to establish a secure handshake with the primary authentication servers.\n\n— DevTracker Team",
    type: "critical"
  },
  PERMISSION_DENIED: {
    title: "Permission Denied",
    message: "Hello,\n\nYou do not have the required administrative clearance to perform this specific action.\n\n— DevTracker Team",
    type: "critical"
  },
  REQUEST_FAILED: {
    title: "Request Failed",
    message: "Hello,\n\nThe platform was unable to process your request due to an internal server anomaly. Our engineers have been notified.\n\n— DevTracker Team",
    type: "critical"
  },
  SUSPENSION_ID_MISMATCH: {
    title: "Account Suspension Notice",
    message: "User: [User Name]\nEmail: [User Email]\n\nReason for Suspension:\nThe account information provided could not be verified. The registered name and email details appear inconsistent with our identity verification standards.\n\nSuspension Type:\nBlocked by AI Security System (Temporary)\n\nNext Step:\nPlease contact support or verify your account information to restore access.\n\n— DevTracker Team\nBy: Origin Core / AI",
    type: "critical"
  },
  SUSPENSION_AI_REVIEW: {
    title: "🚫 Account Suspension Notice",
    message: "User: [User Name]\nEmail: [User Email]\n\nReason for Suspension:\nOur automated security system detected inconsistencies between the registered name and email account details, which may violate platform identity policies.\n\nSuspension Type:\nAI Security Block – Under Review\n\n— DevTracker Team\nBy: Origin Core / AI",
    type: "critical"
  },
  SUSPENSION_AUTO_FLAG: {
    title: "🚫 Account Suspension Notice",
    message: "User: [User Name]\nEmail: [User Email]\n\nReason for Suspension:\nThe account was automatically flagged by our AI moderation system due to mismatched or unverifiable identity information.\n\nSuspension Type:\nTemporary Block (Automated Security Review)\n\n— DevTracker Team\nBy: Origin Core / AI",
    type: "critical"
  },
  SUSPENSION_DASHBOARD: {
    title: "Account Suspended",
    message: "Reason:\nMismatch detected between registered name and email identity.\n\nType:\nAI Security Block (Temporary)\n\nAction:\nVerify your account information to restore access.\n\n— DevTracker Team\nBy: Origin Core / AI",
    type: "critical"
  },

  // 🟣 System / Automation Messages
  DAILY_SUMMARY: {
    title: "Daily Summary",
    message: "Hello,\n\nYour daily activity report has been compiled and is ready for review. This summary includes your latest coding streaks and platform sync statistics.\n\n— DevTracker Team",
    type: "info"
  },
  WEEKLY_REPORT: {
    title: "Weekly Report",
    message: "Hello Developer,\n\nYour weekly performance analysis is now available for download. Your trajectory is currently being mapped by the Origin Core.\n\n— DevTracker Team",
    type: "info"
  },
  BACKUP_COMPLETED_SUCCESS: {
    title: "Backup Completed",
    message: "Hello,\n\nYour account data and development history have been successfully backed up to our secure vault.\n\n— DevTracker Team",
    type: "success"
  },
  PROFILE_INITIALIZED: {
    title: "Profile Initialized",
    message: "Hello Developer,\n\nYour profile setup is complete. Basic configuration has been successfully initialized and your workspace is ready.\n\n— DevTracker Team\nBy: Origin Core / AI",
    type: "success"
  },
  SYNC_COMPLETED_SUCCESS: {
    title: "Sync Completed",
    message: "Hello,\n\nAll connected platform signatures have been successfully synchronized with your primary DevTracker node.\n\n— DevTracker Team",
    type: "success"
  },
  SECURITY_CHECK_COMPLETED: {
    title: "Security Check",
    message: "Hello,\n\nOur periodic system integrity check for your account has been completed. No anomalies were detected.\n\n— DevTracker Team",
    type: "success"
  },
  MAINTENANCE_NOTICE: {
    title: "Maintenance Notice",
    message: "Hello,\n\nPlease be advised that DevTracker will undergo scheduled maintenance to upgrade the Origin Core. Expect brief service interruptions during this window.\n\n— DevTracker Team",
    type: "warning"
  },
  SERVICE_RESTORED: {
    title: "Service Restored",
    message: "Hello,\n\nOur primary infrastructure is now back online and fully operational. Thank you for your patience during the maintenance window.\n\n— DevTracker Team",
    type: "success"
  },
  API_KEY_GENERATED: {
    title: "API Key Generated",
    message: "Hello,\n\nA new secure API access key has been successfully generated for your account. Please store it in a safe location.\n\n— DevTracker Team",
    type: "success"
  },
  INTEGRATION_UPDATED_SUCCESS: {
    title: "Integration Updated",
    message: "Hello,\n\nYour integration connection settings have been updated and verified by our system.\n\n— DevTracker Team",
    type: "success"
  },
  NOTIF_PREFS_UPDATED: {
    title: "Notification Preferences Updated",
    message: "Hello,\n\nYour communication preferences have been successfully updated. You will now receive alerts according to your new settings.\n\n— DevTracker Team",
    type: "success"
  }
};
